import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { api } from "../../utils/api.js";

interface EvalCriteriaData {
  id?: number;
  title: string;
  content: string;
  createdByName?: string;
  mappedJobs?: string[];
}

export function EvalCriteriaForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState<EvalCriteriaData>({ title: "", content: "" });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    api
      .get<EvalCriteriaData>(`/eval-criteria/${id}`)
      .then((d) => setForm(d))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const togglePreview = () => {
    if (!previewMode) {
      const raw = marked.parse(form.content, { async: false }) as string;
      setPreviewHtml(DOMPurify.sanitize(raw));
    }
    setPreviewMode(!previewMode);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      if (isEdit) {
        await api.put(`/eval-criteria/${id}`, {
          title: form.title,
          content: form.content,
        });
        navigate(`/eval-criteria?success=${encodeURIComponent(`"${form.title}" updated`)}`);
      } else {
        await api.post("/eval-criteria", {
          title: form.title,
          content: form.content,
        });
        navigate(`/eval-criteria?success=${encodeURIComponent(`"${form.title}" created`)}`);
      }
    } catch (err) {
      if (err instanceof Error) {
        try {
          const parsed = JSON.parse(err.message) as Record<string, unknown>;
          setError(
            typeof parsed.error === "string"
              ? parsed.error
              : typeof parsed.message === "string"
                ? parsed.message
                : err.message,
          );
        } catch {
          setError(err.message);
        }
      } else {
        setError("Failed to save");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-400 animate-pulse">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/eval-criteria"
          className="text-sm text-primary-700 hover:text-primary-800 mb-2 block"
        >
          &larr; All Criteria
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? "Edit Evaluation Criteria" : "New Evaluation Criteria"}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Define a markdown document that describes the evaluation and scoring system (0-10 scale).
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            placeholder="e.g. AI Infrastructure Engineer Evaluation"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium text-gray-700">
              Criteria Content (Markdown)
            </label>
            <div className="flex rounded-md border border-gray-200 text-xs overflow-hidden">
              <button
                type="button"
                onClick={() => setPreviewMode(false)}
                className={`px-2.5 py-1 transition-colors ${!previewMode ? "bg-gray-100 font-medium text-gray-800" : "text-gray-500 hover:bg-gray-50"}`}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={togglePreview}
                className={`px-2.5 py-1 transition-colors ${previewMode ? "bg-gray-100 font-medium text-gray-800" : "text-gray-500 hover:bg-gray-50"}`}
              >
                Preview
              </button>
            </div>
          </div>

          {previewMode ? (
            <div
              className="prose prose-sm max-w-none rounded-md border border-gray-300 bg-white p-4 min-h-[24rem] overflow-auto"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              required
              rows={20}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              placeholder={`# Evaluation Criteria: Role Name\n\n## Scoring System (0-10)\n\n- **9-10**: Exceptional match...\n- **7-8**: Strong match...\n- **5-6**: Moderate match...\n- **3-4**: Below average...\n- **0-2**: Poor match...\n\n## Evaluation Dimensions\n\n### Technical Skills (Weight: 40%)\n...\n\n### Experience (Weight: 30%)\n...\n\n### Cultural Fit (Weight: 30%)\n...`}
            />
          )}
          <p className="mt-1 text-xs text-gray-400">
            The document must clearly define a 0-10 scoring system. It will be validated by AI on
            save.
          </p>
        </div>

        {isEdit && form.mappedJobs && form.mappedJobs.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Mapped Jobs</label>
            <div className="flex flex-wrap gap-2">
              {form.mappedJobs.map((slug) => (
                <span
                  key={slug}
                  className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700"
                >
                  {slug}
                </span>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-400">
              Job mappings are managed from the Job edit form.
            </p>
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-[var(--action-primary-hover)] disabled:opacity-50 transition-colors"
          >
            {saving ? "Saving..." : isEdit ? "Update Criteria" : "Create Criteria"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/eval-criteria")}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
